import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
    faArrowsToCircle,
    faBoxArchive,
    faCalendar,
    faCalendarPlus,
    faCircleCheck,
    faCircleExclamation,
    faClock,
    faClockRotateLeft,
    faCloud,
    faEnvelope,
    faEnvelopeOpen,
    faFilter,
    faFlag,
    faFolderPlus,
    faGear,
    faHourglassEnd,
    faKeyboard,
    faLayerGroup,
    faList,
    faSearch,
    faSms,
    faTableList,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { ClassicPreset, GetSchemes } from 'rete';
import { ReteContext } from '../../models/rete-context';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { ComponentToolTipDirective } from 'src/app/directives/component-tool-tip.directive';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { DesignerCallbackHandler } from '../../core/designer-callback-handler';
import { ElementVisibility } from '../../models/element-visibilty';
import { ElementVisibilityTypes } from '../../models/Enums/element-visibility-types';
import { WorkflowElementType } from '../../models/Enums/workflow-element-type';
import { WorkflowValidityReport } from '../../models/workflow-validity-report';
import { WorkflowActionNode } from '../../nodes/models/workflow-action-node';
import { WorkflowStepNode } from '../../nodes/models/workflow-step-node';
import { WorkflowTriggerNode } from '../../nodes/models/workflow-trigger-node';
import { WorkflowValidityReportViewerComponentComponent } from './workflow-validity-report-viewer-component/workflow-validity-report-viewer-component.component';
type Schemes = GetSchemes<ClassicPreset.Node, ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>>;

@Component({
    selector: 'app-menu-bar',
    templateUrl: './menu-bar.component.html',
    styleUrls: ['./menu-bar.component.scss'],
    imports: [FaIconComponent, MatTooltipModule, MatButtonModule, MatMenuModule, MatCheckboxModule, ComponentToolTipDirective],
})
export class MenuBarComponent implements AfterViewInit {
    public faFolderPlus = faFolderPlus;
    public emailIcon = faEnvelope;
    public smsIcon = faSms;
    public attributeIcon = faKeyboard;
    public delayExpiredIcon = faHourglassEnd;
    public dateReachIcon = faCalendar;
    public apiCallIcon = faCloud;
    public workflowCompleteIcon = faFlag;
    public attributeUpdateIcon = faTableList;
    public unreadIcon = faEnvelopeOpen;
    public stepIcon = faList;
    public timerIcon = faClock;
    public filterIcon = faFilter;
    public stopTimerIcon = faClockRotateLeft;
    public settingsIcon = faGear;
    public recenterIcon = faArrowsToCircle;
    public rearrangeIcon = faLayerGroup;
    public invalidIcon = faCircleExclamation;
    public validIcon = faCircleCheck;
    public errorIcon = faTriangleExclamation;
    public warningIcon = faTriangleExclamation;
    public reminderCreationIcon = faCalendarPlus;
    public archiveFolderIcon = faBoxArchive;
    public searchIcon = faSearch;

    public WorkflowValidityReportViewerComponent = WorkflowValidityReportViewerComponentComponent;

    public elementVisibilities = [
        { type: ElementVisibilityTypes.ActivationEvent, label: 'Évènements activant une étape' },
        { type: ElementVisibilityTypes.CompleteEvent, label: 'Évènements clôturant une étape' },
        { type: ElementVisibilityTypes.OtherEvent, label: 'Autres évènements' },
    ];

    @Input()
    context: ReteContext;

    /**
     * Indique si l'organisation à souscrit un abonnement aux SMS.
     */
    @Input()
    smsSubscriptionEnabled: boolean = false;

    /**
     * Rapport de validité du workflow.
     */
    @Input()
    validityReport: WorkflowValidityReport = null;

    @Input() addActionCallBack: (type: WorkflowActionTypes) => WorkflowActionNode;

    @Input() addTriggerCallBack: (type: WorkflowTriggerTypes) => WorkflowTriggerNode;

    @Input() addStepCallBack: () => WorkflowStepNode;

    /**
     * Gestionnaire des évènements du designer de workflow.
     */
    @Input()
    public callbackHandler: DesignerCallbackHandler;

    @Output()
    elementVisibilityChanged = new EventEmitter<ElementVisibility>();

    @Output()
    manageSettingsClick = new EventEmitter();

    @Output()
    recenterClick = new EventEmitter();

    @Output()
    rearrangeClick = new EventEmitter();

    @Output()
    searchClick = new EventEmitter();

    @Output()
    validityElementClick = new EventEmitter<{ elementType: WorkflowElementType; elementId: number }>();

    @ViewChild('stepDraggableElement') stepDraggableElement!: ElementRef;

    @ViewChild('folderCreationTriggerDraggableElement') folderCreationTriggerDraggableElement!: ElementRef;

    @ViewChild('attributeUpdatedTriggerDraggableElement') attributeUpdatedTriggerDraggableElement!: ElementRef;

    @ViewChild('dateReachedTriggerDraggableElement') dateReachedTriggerDraggableElement!: ElementRef;

    @ViewChild('delayExpiredTriggerDraggableElement') delayExpiredTriggerDraggableElement!: ElementRef;

    @ViewChild('timerTriggerDraggableElement') timerTriggerDraggableElement!: ElementRef;

    @ViewChild('sendMailActionDraggableElement') sendMailActionDraggableElement!: ElementRef;

    @ViewChild('sendSmsActionDraggableElement') sendSmsActionDraggableElement!: ElementRef;

    @ViewChild('callApiActionDraggableElement') callApiActionDraggableElement!: ElementRef;

    @ViewChild('completeWorkflowActionDraggableElement') completeWorkflowActionDraggableElement!: ElementRef;

    @ViewChild('updateAttributeActionDraggableElement') updateAttributeActionDraggableElement!: ElementRef;

    @ViewChild('flagUnreadActionDraggableElement') flagUnreadActionDraggableElement!: ElementRef;

    @ViewChild('stopTimerActionDraggableElement') stopTimerActionDraggableElement!: ElementRef;

    @ViewChild('reminderCreationActionDraggableElement') reminderCreationActionDraggableElement!: ElementRef;

    @ViewChild('archiveFolderActionDraggableElement') archiveFolderActionDraggableElement!: ElementRef;

    /**
     * Appellé lorsque la vue du composant a fini de s'initialiser.
     */
    ngAfterViewInit(): void {
        this.context.dock.addNodeCreationCapabilityToElement(this.stepDraggableElement.nativeElement as HTMLElement, this.createStepNodeInstance.bind(this));

        this.context.dock.addNodeCreationCapabilityToElement(
            this.folderCreationTriggerDraggableElement.nativeElement as HTMLElement,
            this.createFolderCreationTriggerNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(
            this.attributeUpdatedTriggerDraggableElement.nativeElement as HTMLElement,
            this.createAttributeChangedTriggerNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(
            this.dateReachedTriggerDraggableElement.nativeElement as HTMLElement,
            this.createDateReachedTriggerNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(
            this.delayExpiredTriggerDraggableElement.nativeElement as HTMLElement,
            this.createDelayExpiredTriggerNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(this.timerTriggerDraggableElement.nativeElement as HTMLElement, this.createTimerTriggerNodeInstance.bind(this));

        this.context.dock.addNodeCreationCapabilityToElement(this.sendMailActionDraggableElement.nativeElement as HTMLElement, this.createSendMailActionNodeInstance.bind(this));

        if (this.smsSubscriptionEnabled === true) {
            this.context.dock.addNodeCreationCapabilityToElement(this.sendSmsActionDraggableElement.nativeElement as HTMLElement, this.createSendSmsActionNodeInstance.bind(this));
        }

        this.context.dock.addNodeCreationCapabilityToElement(this.callApiActionDraggableElement.nativeElement as HTMLElement, this.createCallApiActionNodeInstance.bind(this));

        this.context.dock.addNodeCreationCapabilityToElement(
            this.completeWorkflowActionDraggableElement.nativeElement as HTMLElement,
            this.createCompleteWorkflowActionNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(
            this.updateAttributeActionDraggableElement.nativeElement as HTMLElement,
            this.createUpdateAttributeActionNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(
            this.flagUnreadActionDraggableElement.nativeElement as HTMLElement,
            this.createFlagUnreadActionNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(this.stopTimerActionDraggableElement.nativeElement as HTMLElement, this.createStopTimerActionNodeInstance.bind(this));

        this.context.dock.addNodeCreationCapabilityToElement(
            this.reminderCreationActionDraggableElement.nativeElement as HTMLElement,
            this.createReminderCreationActionNodeInstance.bind(this)
        );

        this.context.dock.addNodeCreationCapabilityToElement(
            this.archiveFolderActionDraggableElement.nativeElement as HTMLElement,
            this.createArchiveFolderActionNodeInstance.bind(this)
        );
    }

    public onElementVisibilityChange(elementType: ElementVisibilityTypes, ChangeEvent: MatCheckboxChange) {
        this.elementVisibilityChanged.emit(new ElementVisibility(elementType, ChangeEvent.checked));
    }

    public onManageSettingsClick() {
        this.manageSettingsClick.emit();
    }

    public onRecenterClick() {
        this.recenterClick.emit();
    }

    public onRearrangeClick() {
        this.rearrangeClick.emit();
    }

    public onSearchClick() {
        this.searchClick.emit();
    }

    public onValidityElementClick(sourceType: WorkflowElementType, sourceId: number) {
        console.log('Menu-bar onValidityElementClick', sourceType, sourceId);
        this.validityElementClick.emit({ elementType: sourceType, elementId: sourceId });
    }

    public createStepNodeInstance(): WorkflowStepNode {
        return this.addStepCallBack();
    }

    public createFolderCreationTriggerNodeInstance(): WorkflowTriggerNode {
        return this.addTriggerCallBack(WorkflowTriggerTypes.FolderCreated);
    }

    public createAttributeChangedTriggerNodeInstance(): WorkflowTriggerNode {
        return this.addTriggerCallBack(WorkflowTriggerTypes.AttributeChanged);
    }

    public createDateReachedTriggerNodeInstance(): WorkflowTriggerNode {
        return this.addTriggerCallBack(WorkflowTriggerTypes.DateReached);
    }

    public createDelayExpiredTriggerNodeInstance(): WorkflowTriggerNode {
        return this.addTriggerCallBack(WorkflowTriggerTypes.DelayExpired);
    }

    public createTimerTriggerNodeInstance(): WorkflowTriggerNode {
        return this.addTriggerCallBack(WorkflowTriggerTypes.Timer);
    }

    public createSendMailActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.SendMail);
    }

    public createSendSmsActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.SendSms);
    }

    public createCallApiActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.ApiCall);
    }

    public createCompleteWorkflowActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.CompleteWorkflow);
    }

    public createUpdateAttributeActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.UpdateAttribute);
    }

    public createFlagUnreadActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.SwitchFolderAsUnread);
    }

    public createStopTimerActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.TimerStop);
    }

    public createReminderCreationActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.ReminderCreation);
    }

    public createArchiveFolderActionNodeInstance(): WorkflowActionNode {
        return this.addActionCallBack(WorkflowActionTypes.ArchiveFolder);
    }
}
